/**
 * Dashboard Page (Overview)
 *
 * Main dashboard view with usage chart, stats, and quick actions.
 *
 * @module pages/DashboardPage
 */

import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { usePageTitle, useUsage, usePreviousMonthUsage, usePermissions, getLastNDays, getDayName } from '@/hooks';
import { Card, CardHeader, Button, Spinner, Tooltip } from '@/components/ui';
import {
  BarChart3,
  Zap,
  ArrowRight,
  Plus,
  Eye,
  Headphones,
  TrendingUp,
  TrendingDown,
  Clock,
  RefreshCw,
  AlertCircle,
} from 'lucide-react';
import { formatNumber } from '@/lib/utils';
import { PLAN_RATE_LIMITS } from '@/lib/plans';

// =============================================================================
// Component
// =============================================================================

export function DashboardPage() {
  usePageTitle('Dashboard');

  const { user, organization } = useAuth();
  const { canManageApiKeys } = usePermissions();
  const { usage, loading, refresh, canRefresh, refreshing } = useUsage();
  const { previousTotal, loading: previousLoading } = usePreviousMonthUsage();

  // Get plan limits
  const planId = organization?.planId || 'free';
  const planLimits = PLAN_RATE_LIMITS[planId] || PLAN_RATE_LIMITS.free;
  const isFree = planId === 'free';

  // Use organization's actual bucket limit for paid plans, or plan default for free
  const limit = isFree ? planLimits.requestsPerMonth : (organization?.currentBucketMax ?? planLimits.requestsPerMonth);

  // Current usage
  const thisMonth = usage?.totalRequests || 0;
  const lastMonth = previousTotal || 0;

  // Calculate percent change
  const percentChange =
    lastMonth > 0
      ? ((thisMonth - lastMonth) / lastMonth) * 100
      : thisMonth > 0
        ? 100
        : 0;

  // Usage percentage
  const usagePercent = limit > 0 ? Math.min((thisMonth / limit) * 100, 100) : 0;

  // Get last 7 days for chart
  const last7Days = getLastNDays(7);
  const dailyUsage = last7Days.map((dateKey) => ({
    day: getDayName(dateKey),
    date: dateKey,
    requests: usage?.daily[dateKey] || 0,
  }));

  const maxRequests = Math.max(...dailyUsage.map((d) => d.requests), 1);
  const hasDailyData = dailyUsage.some((d) => d.requests > 0);

  // Last updated time
  const lastUpdated = usage?.lastUpdated
    ? new Date(usage.lastUpdated).toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      })
    : null;

  const isLoading = loading || previousLoading;

  // Handle refresh click - only works for paid plans
  const handleRefresh = () => {
    if (canRefresh) {
      refresh();
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Welcome Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-display font-semibold text-gray-900">
            Welcome back, {user?.firstName || user?.displayName?.split(' ')[0] || 'there'}!
          </h1>
          <p className="text-gray-500 mt-1">
            Here's what's happening with your API usage.
            {lastUpdated && (
              <span className="text-gray-400 ml-2">Last updated: {lastUpdated}</span>
            )}
          </p>
        </div>

        {/* Refresh button - always visible, disabled for free plans */}
        <Tooltip
          content={
            !canRefresh ? (
              <span>
                Upgrade to Pro for on-demand refresh with real-time usage updates.{' '}
                <a
                  href="/settings/billing"
                  className="underline text-brand-300 hover:text-brand-200"
                >
                  Upgrade now →
                </a>
              </span>
            ) : null
          }
          position="bottom"
        >
          <Button
            variant="secondary"
            size="sm"
            onClick={handleRefresh}
            disabled={!canRefresh || refreshing}
            leftIcon={<RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />}
          >
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </Button>
        </Tooltip>
      </div>

      {/* Usage Alert (if approaching limit on free plan) */}
      {!isLoading && usagePercent >= 80 && planId === 'free' && (
        <Card className="bg-gradient-to-r from-brand-50 to-orange-50 border-brand-200">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h3 className="font-semibold text-gray-900">
                You've used {Math.round(usagePercent)}% of your monthly quota
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                Upgrade to Pro for higher limits and advanced features.
              </p>
            </div>
            <Link to="/settings/billing">
              <Button rightIcon={<ArrowRight className="h-4 w-4" />}>Upgrade Now</Button>
            </Link>
          </div>
        </Card>
      )}

      {/* 7-Day Usage Chart */}
      {isLoading ? (
        <Card>
          <CardHeader title="Daily Requests" description="API requests over the past 7 days" />
          <div className="h-64 flex items-center justify-center">
            <Spinner />
          </div>
        </Card>
      ) : thisMonth > 0 && hasDailyData ? (
        <Card>
          <CardHeader title="Daily Requests" description="API requests over the past 7 days" />
          <div className="h-64 flex items-end justify-between gap-2">
            {dailyUsage.map((day) => (
              <div key={day.date} className="flex-1 flex flex-col items-center gap-2">
                <div className="w-full flex flex-col items-center justify-end h-48">
                  <div
                    className="w-full max-w-[40px] bg-brand-500 rounded-t-md transition-all hover:bg-brand-600"
                    style={{
                      height:
                        day.requests > 0
                          ? `${Math.max((day.requests / maxRequests) * 100, 2)}%`
                          : '2px',
                    }}
                  />
                </div>
                <div className="text-center">
                  <p className="text-xs font-medium text-gray-600">{day.day}</p>
                  <p className="text-xs text-gray-400">{formatNumber(day.requests)}</p>
                </div>
              </div>
            ))}
          </div>
        </Card>
      ) : thisMonth > 0 && !hasDailyData ? (
        <Card>
          <CardHeader title="Daily Requests" description="API requests over the past 7 days" />
          <div className="h-64 flex flex-col items-center justify-center text-center px-4">
            <BarChart3 className="h-12 w-12 text-gray-300 mb-3" />
            <p className="text-sm text-gray-500">
              Daily breakdown is being collected.
            </p>
            <p className="text-xs text-gray-400 mt-1">
              Check back soon for detailed daily statistics.
            </p>
          </div>
        </Card>
      ) : null}

      {/* Stats Grid - 4 cards */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {/* This Month */}
        <Card>
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">This Month</p>
              {isLoading ? (
                <Spinner size="sm" className="mt-2" />
              ) : (
                <p className="text-2xl font-semibold text-gray-900 mt-1">
                  {formatNumber(thisMonth)}
                </p>
              )}
            </div>
            <div className="p-2 bg-brand-100 rounded-lg">
              <BarChart3 className="h-5 w-5 text-brand-600" />
            </div>
          </div>
          {!isLoading && percentChange !== 0 && (
            <p
              className={`text-sm flex items-center gap-1 mt-2 ${
                percentChange > 0 ? 'text-success-600' : 'text-gray-500'
              }`}
            >
              {percentChange > 0 ? (
                <TrendingUp className="h-4 w-4" />
              ) : (
                <TrendingDown className="h-4 w-4" />
              )}
              {percentChange > 0 ? '+' : ''}
              {percentChange.toFixed(1)}% from last month
            </p>
          )}
        </Card>

        {/* Last Month */}
        <Card>
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Last Month</p>
              {isLoading ? (
                <Spinner size="sm" className="mt-2" />
              ) : (
                <p className="text-2xl font-semibold text-gray-900 mt-1">
                  {formatNumber(lastMonth)}
                </p>
              )}
            </div>
            <div className="p-2 bg-gray-100 rounded-lg">
              <Clock className="h-5 w-5 text-gray-600" />
            </div>
          </div>
        </Card>

        {/* Request Limit */}
        <Card>
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">
                {isFree ? 'Lifetime Requests' : 'Monthly Requests'}
              </p>
              <p className="text-2xl font-semibold text-gray-900 mt-1">{formatNumber(limit)}</p>
            </div>
            <div className="p-2 bg-gray-100 rounded-lg">
              <AlertCircle className="h-5 w-5 text-gray-600" />
            </div>
          </div>
        </Card>

        {/* Usage % */}
        <Card>
          <div className="flex items-start justify-between">
            <div className="w-full">
              <p className="text-sm font-medium text-gray-500">Usage</p>
              {isLoading ? (
                <Spinner size="sm" className="mt-2" />
              ) : (
                <>
                  <p className="text-2xl font-semibold text-gray-900 mt-1">
                    {Math.round(usagePercent)}%
                  </p>
                  <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                    <div
                      className={`h-2 rounded-full transition-all ${
                        usagePercent >= 90
                          ? 'bg-error-500'
                          : usagePercent >= 75
                            ? 'bg-warning-500'
                            : 'bg-brand-500'
                      }`}
                      style={{ width: `${usagePercent}%` }}
                    />
                  </div>
                </>
              )}
            </div>
          </div>
        </Card>
      </div>

      {/* Main Content Grid */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Quick Actions */}
        <Card>
          <CardHeader title="Quick Actions" />
          <div className="space-y-3">
            {canManageApiKeys ? (
              <Link
                to="/keys"
                className="flex items-center justify-between p-3 rounded-lg border border-gray-200 hover:border-brand-300 hover:bg-brand-50/50 transition-colors group"
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-brand-100 rounded-lg text-brand-600 group-hover:bg-brand-200 transition-colors">
                    <Plus className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">Create API Key</p>
                    <p className="text-sm text-gray-500">Generate a new key for your application</p>
                  </div>
                </div>
                <ArrowRight className="h-5 w-5 text-gray-400 group-hover:text-brand-500 transition-colors" />
              </Link>
            ) : (
              <>
                <Link
                  to="/keys"
                  className="flex items-center justify-between p-3 rounded-lg border border-gray-200 hover:border-brand-300 hover:bg-brand-50/50 transition-colors group"
                >
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-brand-100 rounded-lg text-brand-600 group-hover:bg-brand-200 transition-colors">
                      <Eye className="h-5 w-5" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">View API Keys</p>
                      <p className="text-sm text-gray-500">See your organization's API keys</p>
                    </div>
                  </div>
                  <ArrowRight className="h-5 w-5 text-gray-400 group-hover:text-brand-500 transition-colors" />
                </Link>
              </>
            )}
          </div>
        </Card>

        {/* Upgrade CTA for Free Users */}
        {planId === 'free' && (
          <Card className="bg-gradient-to-br from-brand-50 to-purple-50 border-brand-200">
            <CardHeader
              title="Unlock More Power"
              description="Upgrade your plan for premium features"
            />
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-brand-100 rounded-lg">
                  <Zap className="h-4 w-4 text-brand-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">Higher Rate Limits</p>
                  <p className="text-xs text-gray-500">Up to 500 requests/second</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="p-2 bg-brand-100 rounded-lg">
                  <Headphones className="h-4 w-4 text-brand-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">Priority Support</p>
                  <p className="text-xs text-gray-500">Get help when you need it</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="p-2 bg-brand-100 rounded-lg">
                  <BarChart3 className="h-4 w-4 text-brand-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">Advanced Analytics</p>
                  <p className="text-xs text-gray-500">Real-time usage insights</p>
                </div>
              </div>
              <Link to="/settings/billing" className="block pt-2">
                <Button className="w-full" rightIcon={<ArrowRight className="h-4 w-4" />}>
                  Upgrade Now
                </Button>
              </Link>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}